import React, {useCallback, useEffect, useState} from 'react';
import Grid from "@mui/material/Grid";
import {Autocomplete, FormHelperText, InputAdornment, TextField} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import dayjs from "dayjs";
import {formValidations} from "../CVTemplate";
import OrangeSwitch from "../../OrangeSwitch";
import FormControl from "@mui/material/FormControl";
import Chip from "@mui/material/Chip";

const NoticePeriod = [
    {
        value: "",
        label: "Select notice period",
    },
    {
        value: "2 weeks",
        label: "2 weeks",
    },
    {
        value: "4 weeks",
        label: "4 weeks",
    },
    {
        value: "30 Days",
        label: "30 Days",
    },
    {
        value: "1 Calendar month",
        label: "1 Calendar month",
    },
    {
        value: "Immediately available",
        label: "Immediately available",
    },
    {
        value: "Other",
        label: "Other",
    },
];

export default function AdditionalQuestions({additionalQuestions, updateCV, userHasClickedNext, readOnly, jobPlacement, workModel}) {
    const style = {
        "& label.Mui-focused": {
            color: "#FF6600",
        },
        "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
                borderColor: "#FF6600",
            },
        },
    };

    const [cities, setCities] = useState(
        typeof additionalQuestions.cityWork === 'string'
            ? additionalQuestions.cityWork.split(',').map(item => item.trim())
            : []
    );
    const [formData, setFormData] = useState(additionalQuestions);
    const [requiredData, setRequiredData] = useState({
        cityWork: formData.cityWork,
        currentSalary: formData.currentSalary,
        expectedSalary: formData.expectedSalary,
        whyWorkWebtonic: formData.whyWorkWebtonic,
        consentCriminalCheck: formData.consentCriminalCheck,
        consentCreditCheck: formData.consentCreditCheck,
        noticePeriod: formData.noticePeriod,
        popi: formData.popi,
    })

    const handleChange = async (e) => {
        let {name, value, checked} = e.target;

        const updateValue = (["hybrid", "consentCriminalCheck", "consentCreditCheck", "popi"].includes(name)) ? checked : value;
        if (name === "hybrid") {
            const updatedFormData = { ...formData, [name]: updateValue };
            setFormData(updatedFormData);
            updateCV('additionalQuestions', updatedFormData);
        } else {
            manageUpdates(name, updateValue)
        }
    };

    const handleChangeCityWork = async (value) => {
        const selectedCities = Array.isArray(value) ? value : value.split(',');
        setCities(selectedCities);
        manageUpdates("cityWork", selectedCities.join(','));
    }

    const manageUpdates = (name, newValue) => {
        setFormData((prevFormValues) => ({
            ...prevFormValues,
            [name]: newValue,
        }));
        setRequiredData((prevFormValues) => ({
            ...prevFormValues,
            [name]: newValue,
        }));
        updateCV('additionalQuestions', {
            ...formData,
            [name]: newValue
        });
    }

    const checkReqData = useCallback(() => {
        formValidations[5].valid = !!(!Object.values(requiredData).includes("") && (requiredData.consentCriminalCheck && requiredData.consentCreditCheck && requiredData.popi));
    }, [requiredData]);

    useEffect(() => {
        checkReqData();
    }, [checkReqData, formData, additionalQuestions]);

    return (
        <Grid container direction={"row"} spacing={2} padding={2}>
            <Grid item xs={12}>
                <h3 data-testid={"Heading"}>Additional Questions</h3>
            </Grid>
            <Grid item xs={6}>
                <FormControl sx={style} className="inputWidth">
                    <Autocomplete
                        data-testid="cityWork"
                        multiple
                        id="city-autocomplete"
                        options={jobPlacement || []}
                        value={cities}
                        onChange={(event, newValue) => handleChangeCityWork(newValue)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                data-testid="cityWorkText"
                                variant={readOnly ? "standard" : "outlined"}
                                label="Cities prepared to work in"
                                error={userHasClickedNext && cities.length === 0}
                            />
                        )}
                        renderTags={(selectedCities, getTagProps) =>
                            selectedCities.map((city, index) => {
                                const tagProps = getTagProps({ index });
                                return (
                                    <Chip
                                        key={city}
                                        label={city}
                                        // Conditionally render the `Chip` without `onDelete` if `readOnly` is true
                                        {...(readOnly ? {} : { onDelete: tagProps.onDelete })}
                                        disabled={false} // Ensure chip itself is not disabled
                                        style={{ margin: 2 }}
                                    />
                                );
                            })
                        }
                    />
                    {userHasClickedNext && cities.length === 0 && (
                        <FormHelperText error>Please select at least one location.</FormHelperText>
                    )}
                </FormControl>
            </Grid>
            <Grid item xs={6}>
            </Grid>
            <Grid item xs={8}>
                <FormControlLabel
                    control={<OrangeSwitch/>}
                    label={`Are you happy to work in a ${workModel} environment?`}
                    labelPlacement="end"
                    value={formData.hybrid}
                    data-testid={"hybrid"}
                    checked={formData.hybrid}
                    onChange={e => handleChange(e)}
                    name={"hybrid"}
                />
            </Grid>
            <Grid item xs={4}>

            </Grid>

            <Grid item xs={8}>
                <TextField label="Current Salary (Net per annum)"
                           InputProps={{
                               startAdornment: (<InputAdornment position="start">R</InputAdornment>),
                               inputMode: "numeric",
                               pattern: "[0-9]*"
                           }}
                           sx={style}
                           style={{width: "100%"}}
                           required
                           variant={readOnly ? "standard" : "outlined"}
                           error={userHasClickedNext && !formData.currentSalary}
                           value={formData.currentSalary}
                           name={"currentSalary"}
                           data-testid={"currentSalary"}
                           onChange={e => {
                               e.target.value = e.target.value.replace(/\D/g, "");
                               handleChange(e)
                           }}>
                </TextField>
            </Grid>
            <Grid item xs={4}>

            </Grid>

            <Grid item xs={8}>
                <TextField label="Expected Salary (Net per annum)"
                           InputProps={{
                               startAdornment: (<InputAdornment position="start">R</InputAdornment>),
                               inputMode: "numeric",
                               pattern: "[0-9]*"
                           }}
                           sx={style}
                           style={{width: "100%"}}
                           required
                           variant={readOnly ? "standard" : "outlined"}
                           error={userHasClickedNext && !formData.expectedSalary}
                           value={formData.expectedSalary}
                           data-testid={"expectedSalary"}
                           name={"expectedSalary"}
                           onChange={e => {
                               e.target.value = e.target.value.replace(/\D/g, "");
                               handleChange(e)
                           }}>
                    >
                </TextField>
            </Grid>
            <Grid item xs={4}>
            </Grid>

            <Grid item xs={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker label={"Last increase date"}
                                sx={style}
                                format="DD/MM/YYYY"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        variant: readOnly ? "standard" : "outlined",
                                        error: false,
                                        inputProps: {"data-testid": "increaseDate"}
                                    }
                                }}
                                error={false}
                                style={{width: "100%"}}
                                onChange={e => formData.lastIncreaseDate = (e)}
                                defaultValue={dayjs(formData.lastIncreaseDate)}
                    />
                </LocalizationProvider>
            </Grid>
            <Grid item xs={9}>
            </Grid>

            <Grid item xs={8}>
                <TextField label="Why would you like to work at Webtonic"
                           multiline
                           minRows={4}
                           sx={style}
                           style={{width: "100%"}}
                           value={formData.whyWorkWebtonic}
                           required
                           variant={readOnly ? "standard" : "outlined"}
                           error={userHasClickedNext && !formData.whyWorkWebtonic}
                           data-testid={"whyWorkWebtonic"}
                           name={"whyWorkWebtonic"}
                           onChange={e => handleChange(e)}>
                </TextField>
            </Grid>
            <Grid item xs={4}>

            </Grid>

            <Grid item xs={8}>
                <FormControlLabel
                    control={<OrangeSwitch/>}
                    label="Do you provide consent in doing a criminal check?"
                    labelPlacement="end"
                    style={{color: (userHasClickedNext && !formData.consentCriminalCheck) ? '#da1b1b' : '#000000'}}
                    value={formData.consentCriminalCheck}
                    required
                    error={userHasClickedNext && !formData.consentCriminalCheck}
                    checked={formData.consentCriminalCheck}
                    onChange={e => handleChange(e)}
                    name={"consentCriminalCheck"}
                />
            </Grid>
            <Grid item xs={4}>

            </Grid>

            <Grid item xs={8}>
                <FormControlLabel
                    control={<OrangeSwitch/>}
                    label="Do you provide consent in doing a credit check?"
                    labelPlacement="end"
                    required
                    style={{color: (userHasClickedNext && !formData.consentCreditCheck) ? '#da1b1b' : '#000000'}}
                    value={formData.consentCreditCheck}
                    checked={formData.consentCreditCheck}
                    onChange={e => handleChange(e)}
                    name={"consentCreditCheck"}
                />
            </Grid>
            <Grid item xs={4}>
            </Grid>

            <Grid item xs={3}>
                <TextField label={"What is your current notice period?"}
                           sx={style}
                           select
                           value={formData.noticePeriod}
                           variant={readOnly ? "standard" : "outlined"}
                           name={"noticePeriod"}
                           required
                           error={userHasClickedNext && !formData.noticePeriod}
                           onChange={e => handleChange(e)}
                           style={{width: "100%"}}>
                    {NoticePeriod.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid item xs={9}>

            </Grid>

            <Grid item xs={12}>
                <FormControlLabel
                    control={<Checkbox checked={readOnly} data-testid={"popi"} name={'popi'} onClick={(e) => {
                        handleChange(e)
                    }} id={"popi-act-checkbox"}
                                       sx={{
                                           color: "#FF6600",
                                           '&.Mui-checked': {
                                               color: "#FF6600",
                                           },
                                       }}/>}
                    label="By “submitting” this form, you hereby provide TonicHub with consent to process your personal information. You further acknowledge that TonicHub may obtain personal information from third parties for the purpose of fulfilling its agreement with you and agree that your consent extents to obtaining and processing such information. Click here to view the full details to the POPI ACT."
                    labelPlacement="end"
                />
            </Grid>
        </Grid>
    )
}